import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_738c4bc1-ee18-4cfa-bcd6-d8b6ffe0137e/artifacts/8yk2zlr5_1D5D851C-FB58-4F83-897F-7E5F2D98C34E.PNG";

export default function JoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/invites/${token}/validate`);
        setInvite(res.data);
        setName(res.data.name || "");
      } catch {
        setInvalid(true);
      }
      setLoading(false);
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/invites/${token}/accept`, { name, password });
      localStorage.setItem("token", res.data.token);
      toast.success("Account created! Welcome to Tides Hub.");
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Invalid Invite Link</h2>
            <p className="text-slate-500 mb-6">This invite link is invalid or has already been used.</p>
            <Button onClick={() => navigate("/login")} className="bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Tides Team Exteriors" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F4C81]">Join Tides Hub</h1>
          <p className="text-slate-500 mt-1">You've been invited to join the team</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800">Valid Invite</p>
                <p className="text-xs text-emerald-600">
                  {invite.email} &middot; Role: {invite.role_label}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="h-12"
                  data-testid="join-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={invite.email} disabled className="h-12 bg-slate-50" data-testid="join-email" />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    className="h-12 pr-12"
                    data-testid="join-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  className="h-12"
                  data-testid="join-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#0F4C81] hover:bg-[#0D4270]"
                disabled={submitting}
                data-testid="join-submit"
              >
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                Create Account
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
